import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useCookies } from 'react-cookie';
import AdminSharedPresetPanel from './AdminSharedPresetPanel';
import HoldAdminManualModal from './HoldAdminManualModal';
import OpenPositionsSnapshotSection from './OpenPositionsSnapshotSection';
import SymbolSearchPopup from './SymbolSearchPopup';
import { pickEscapeAccountFields } from './escapeAccountSettings';
import {
  buildEscapeDefaultFlat,
  buildEscapeFlatFromSettingsSplit,
  buildEscapeSplitPayload,
} from './escapeStartSettingsShared';
import {
  applyDecimalPctInputChange,
  decimalPctToNumber,
  formatDecimalPctInputValue,
  normalizeDecimalPctOnBlur,
} from './decimalPctInput';
import { bandHoldShowsProfitRoi, isBandHybridHoldMode } from './bandBulkFieldConfig';

function snapshotAnchorRect(el) {
  if (typeof el?.getBoundingClientRect !== 'function') {
    return null;
  }
  const r = el.getBoundingClientRect();
  return {
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
    bottom: r.bottom,
    right: r.right,
  };
}

function roundPct2(val) {
  const n = parseFloat(val);
  return Number.isNaN(n) ? 0 : Number(n.toFixed(2));
}

function normalizeKpModeValue(val) {
  return val === 'confirm' ? 'confirm' : 'trigger';
}

function KpModeSelect({ label, field, value, patchFlat, syncPlusEntry = false }) {
  const val = normalizeKpModeValue(value);
  return (
    <>
      <div className="leverage_item">{label}</div>
      <select
        className="select_samaria"
        value={val}
        onChange={(e) => {
          const patch = { [field]: e.target.value };
          if (syncPlusEntry && field === 'escape_entry_kp_mode') {
            patch.plus_entry_mode = e.target.value;
          }
          patchFlat(patch);
        }}
      >
        <option value="trigger">발생</option>
        <option value="confirm">확정</option>
      </select>
    </>
  );
}

function EscapeKpModeSelectors({
  entryValue,
  exitValue,
  entryField = 'escape_entry_kp_mode',
  exitField = 'escape_exit_kp_mode',
  entryLabel = '진입 기준',
  exitLabel = '탈출 정리 기준',
  middleValue,
  middleField,
  middleLabel,
  showEntry = true,
  showExit = true,
  showMiddle = true,
  patchFlat,
}) {
  const entryVal = normalizeKpModeValue(entryValue);
  const exitVal = normalizeKpModeValue(exitValue);
  const middleVal = normalizeKpModeValue(middleValue);
  const hasMiddle = Boolean(middleField);
  return (
    <div className="leverage_container">
      {showEntry && (
        <>
          <div className="leverage_item">{entryLabel}</div>
          <select
            className="select_samaria"
            value={entryVal}
            onChange={(e) => patchFlat({ [entryField]: e.target.value })}
          >
            <option value="trigger">발생</option>
            <option value="confirm">확정</option>
          </select>
        </>
      )}
      {hasMiddle && showMiddle && (
        <>
          <div className="leverage_item">{middleLabel}</div>
          <select
            className="select_samaria"
            value={middleVal}
            onChange={(e) => patchFlat({ [middleField]: e.target.value })}
          >
            <option value="trigger">발생</option>
            <option value="confirm">확정</option>
          </select>
        </>
      )}
      {showExit && (
        <>
          <div className="leverage_item">{exitLabel}</div>
          <select
            className="select_samaria"
            value={exitVal}
            onChange={(e) => patchFlat({ [exitField]: e.target.value })}
          >
            <option value="trigger">발생</option>
            <option value="confirm">확정</option>
          </select>
        </>
      )}
    </div>
  );
}

function GridUtSignalSettings({ flat, patchFlat }) {
  const checking = Number(flat.entry_checking);
  const checkingVal = checking === -2 ? -2 : -1;
  return (
    <div style={{ marginTop: '8px' }}>
      <table
        className="admin-wz-bulk-start__entry-table escape-grid-ut-signal-table"
        style={{ width: '100%', fontSize: '13px' }}
      >
        <thead>
          <tr>
            <th>UT 신호</th>
            <th>캔들타입</th>
            <th>Sensitivity</th>
            <th>ATR</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <select
                className="select_samaria escape-grid-ut-signal-table__control"
                value={checkingVal}
                onChange={(e) =>
                  patchFlat({ entry_checking: Number(e.target.value), entry_mode: 'ut' })
                }
              >
                <option value={-1}>발생</option>
                <option value={-2}>확정</option>
              </select>
            </td>
            <td>
              <select
                className="select_samaria escape-grid-ut-signal-table__control"
                value={flat.candle_type_entry || 'haikin'}
                onChange={(e) =>
                  patchFlat({ candle_type_entry: e.target.value, entry_mode: 'ut' })
                }
              >
                <option value="haikin">하이킨</option>
                <option value="normal">일반</option>
              </select>
            </td>
            <td>
              <input
                type="number"
                className="leverage_input_basic escape-grid-ut-signal-table__control"
                min="1"
                value={flat.sensitivity ?? 2}
                onChange={(e) =>
                  patchFlat({ sensitivity: Number(e.target.value), entry_mode: 'ut' })
                }
              />
            </td>
            <td>
              <input
                type="number"
                className="leverage_input_basic escape-grid-ut-signal-table__control"
                min="1"
                value={flat.atr_period ?? 2}
                onChange={(e) =>
                  patchFlat({ atr_period: Number(e.target.value), entry_mode: 'ut' })
                }
              />
            </td>
          </tr>
        </tbody>
      </table>
      <p style={{ fontSize: '12px', opacity: 0.85, margin: '6px 0 0' }}>
        시그널(수동) 그리드의 첫·추가 진입은 위 UT 설정과 진입 봉을 사용합니다. 탈출 스윙은 아래 킨포인트 기준을
        따릅니다. 보조기준 EMA는 그리드 첫·추가 진입에 적용됩니다.
      </p>
    </div>
  );
}

function NonPlusEscapeKpSelectors({ flat, patchFlat }) {
  const patchEntryKp = (patch) => {
    const next = { ...patch };
    if (next.escape_entry_kp_mode !== undefined) {
      next.escape_exit_kp_mode = next.escape_entry_kp_mode;
    }
    patchFlat(next);
  };
  return (
    <>
      <EscapeKpModeSelectors
        entryValue={flat.escape_entry_kp_mode}
        exitValue={flat.escape_swing_exit_kp_mode}
        entryField="escape_entry_kp_mode"
        exitField="escape_swing_exit_kp_mode"
        entryLabel="진입 기준"
        exitLabel="탈출 정리 기준"
        patchFlat={patchEntryKp}
        middleValue={flat.escape_swing_entry_kp_mode}
        middleField="escape_swing_entry_kp_mode"
        middleLabel="탈출 진입 기준"
      />
      <p style={{ fontSize: '12px', opacity: 0.85, margin: '4px 0 0' }}>
        진입 기준=진입봉 진입·반대신호(익절/탈출발동) · 탈출 진입/정리=탈출모드 구동 중 탈출봉 스윙
      </p>
    </>
  );
}

const PROGRAM_VARIANTS = [
  { value: 'oneway', label: '원웨이 이스케이프', enabled: true },
  { value: 'plus', label: '플러스', enabled: true },
  { value: 'dual_regression', label: '양방 회귀', enabled: true },
  { value: 'grid_escape', label: '그리드+이스케이프', enabled: true },
];

const GRID_SETTING_MODES = [
  { value: 'uniform', label: '동일셋팅' },
  { value: 'manual', label: '수동셋팅' },
  { value: 'kinpoint_manual', label: '킨포인트(수동)' },
  { value: 'signal_manual', label: '시그널(수동)' },
];

const GRID_ESCAPE_APPLY_OPTIONS = [
  { value: 'apply', label: '적용' },
  { value: 'skip', label: '미적용' },
];

const GRID_MANUAL_ROW_PAIRS = [
  [1, 2],
  [3, 4],
  [5, 6],
];

function renderGridManualLineCells(n, flat, entryUsdt1, patchFlat) {
  const usdtKey = n === 1 ? 'entry_usdt_1' : `grid_usdt_${n}`;
  const gapKey = n > 1 ? `grid_gap_${n - 1}` : null;
  const usdtVal = n === 1 ? entryUsdt1 : flat[usdtKey];
  const gapVal = gapKey ? flat[gapKey] : null;
  return (
    <>
      <td>{n}</td>
      <td>
        <input
          type="number"
          className="leverage_input_basic"
          style={{ width: '100%' }}
          value={usdtVal ?? 0}
          onChange={(e) => patchFlat({ [usdtKey]: Number(e.target.value) })}
        />
      </td>
      <td>
        {gapKey ? (
          <input
            type="number"
            className="leverage_input_basic"
            style={{ width: '100%' }}
            value={gapVal ?? 0}
            onChange={(e) => patchFlat({ [gapKey]: Number(e.target.value) })}
          />
        ) : (
          '—'
        )}
      </td>
    </>
  );
}

const BOTH_MODE_OPTIONS = [
  { value: 'single', label: 'single' },
  { value: 'double', label: 'double' },
];

const CANDLE_TF_OPTIONS = ['5m', '15m', '30m', '1h', '4h', '12h', '1d'];

const SETLEVEL = 8;

function symbolToUrlName(sym) {
  if (!sym || sym === 'default') return 'default';
  const idx = sym.indexOf('/');
  return idx >= 0 ? sym.substring(0, idx) : sym;
}

function escapeVariantShortLabel(programVariant) {
  const map = {
    oneway: '원웨이',
    plus: '플러스',
    dual_regression: '양방회귀',
    grid_escape: '그리드',
  };
  return map[programVariant] ?? programVariant ?? '';
}

function formatEscapeListItemLabel(item) {
  if (!item) return '';
  if (typeof item === 'string') return item;
  const sym = item.symbol || '';
  const variant = escapeVariantShortLabel(item.program_variant);
  if (!variant) return sym;
  if (item.program_variant === 'oneway' || item.program_variant === 'plus') {
    const accountMode = item.both_mode === 'double' ? 'double' : 'single';
    return `${sym} (${variant} ${accountMode})`;
  }
  return `${sym} (${variant})`;
}

function formatEscapeRefPx(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n >= 1) return n.toFixed(4);
  return n.toFixed(8);
}

function parseEscapeRefPxInput(raw) {
  const trimmed = String(raw ?? '').trim().replace(/,/g, '');
  if (!trimmed) return 0;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : NaN;
}

function pickEscapeRefPxFromSettings(split, bothMode) {
  const main = split?.main_row || {};
  const aux = split?.aux_row || {};
  const isDouble = bothMode === 'double' && Boolean(split?.has_aux);
  const isPlus = main.program_variant === 'plus';
  if (isPlus && isDouble) {
    return {
      highPx: formatEscapeRefPx(main.escape_last_long_entry_px),
      lowPx: formatEscapeRefPx(aux.escape_last_short_entry_px),
      highSource: '메인',
      lowSource: '보조',
    };
  }
  if (isDouble) {
    return {
      highPx: formatEscapeRefPx(aux.escape_last_long_entry_px),
      lowPx: formatEscapeRefPx(main.escape_last_short_entry_px),
      highSource: '보조',
      lowSource: '메인',
    };
  }
  return {
    highPx: formatEscapeRefPx(main.escape_last_long_entry_px),
    lowPx: formatEscapeRefPx(main.escape_last_short_entry_px),
    highSource: '메인',
    lowSource: '메인',
  };
}

export default function SetpageEscape() {
  const [cookies] = useCookies(['uuid']);
  const [uid, setUID] = useState('');
  const [nickname, setNickname] = useState('');
  const [isAdminAccess, setIsAdminAccess] = useState(false);
  const [adminManualTradeAllowed, setAdminManualTradeAllowed] = useState(false);
  const [adminManualModal, setAdminManualModal] = useState(null);
  const [snapshotReloadToken, setSnapshotReloadToken] = useState(0);
  const [memberHasSecondBitget, setMemberHasSecondBitget] = useState(false);
  const [symbolList, setSymbolList] = useState([]);
  const [symbol, setSymbol] = useState('default');
  const [entryItems, setEntryItems] = useState([]);
  const [waitItems, setWaitItems] = useState([]);
  const [tradeStart, setTradeStart] = useState(0);
  const [benefitStop, setBenefitStop] = useState(0);
  const [hasAuxAccount, setHasAuxAccount] = useState(false);
  const [mainBalance, setMainBalance] = useState(0);
  const [secondBalance, setSecondBalance] = useState(0);
  const [doublePnl, setDoublePnl] = useState(0);
  const [totalBenefitUsdt, setTotalBenefitUsdt] = useState(0);
  const [totalBenefitUsdtObj, setTotalBenefitUsdtObj] = useState(0);
  const [totalBenefitCount, setTotalBenefitCount] = useState(0);
  const [totalCurrentCount, setTotalCurrentCount] = useState(0);
  const [totalBenefitStart, setTotalBenefitStart] = useState(0);
  const [totalContinueMode, setTotalContinueMode] = useState('disable');
  const [totalContinueDirection, setTotalContinueDirection] = useState('both');
  const [escapeHighPx, setEscapeHighPx] = useState('0');
  const [escapeLowPx, setEscapeLowPx] = useState('0');
  const [escapeHighPxSource, setEscapeHighPxSource] = useState('메인');
  const [escapeLowPxSource, setEscapeLowPxSource] = useState('메인');

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupAnchor, setPopupAnchor] = useState(null);
  const [isSymbolMenuOpen, setIsSymbolMenuOpen] = useState(false);
  const [isBothMenuOpen, setIsBothMenuOpen] = useState(false);
  const [isVariantMenuOpen, setIsVariantMenuOpen] = useState(false);
  const symbolDropdownRef = useRef(null);
  const bothDropdownRef = useRef(null);
  const variantDropdownRef = useRef(null);
  const lastSettingsSplitRef = useRef(null);
  const settingsLoadSeqRef = useRef(0);

  const [flat, setFlat] = useState(() => buildEscapeDefaultFlat());

  const {
    program_variant,
    both_mode,
    direction,
    entry_candle,
    close_candle,
    entry_standard,
    ema_standard,
    candle_standard,
    escape_auto_refresh_gap_pct,
    escape_hedge_losscut_pct,
    hedge_hold_ratio,
    hedge_hold_mode,
    hedge_hold_roi_pct,
    hedge_hold_switch_pct,
    escape_same_bar_entry_extra,
    escape_first_entry_mode,
    plus_entry_mode,
    escape_entry_kp_mode,
    escape_exit_kp_mode,
    escape_swing_entry_kp_mode,
    escape_swing_exit_kp_mode,
    plus_close_kp_mode,
    entry_usdt_1,
    leverage_info,
    benefit_ratio,
    single_tp_continue_mode,
    double_benefit,
    double_continue,
    total_benefit_usdt,
    total_benefit_usdt_obj,
    total_benefit_count,
    total_current_count,
    total_benefit_start,
    total_continue_mode,
    total_continue_direction,
    dual_trigger_min_profit_pct,
    grid_setting_mode,
    grid_escape_apply,
    grid_line_count,
    grid_gap_pct,
    grid_usdt_per_line,
    grid_usdt_2,
    grid_usdt_3,
    grid_usdt_4,
    grid_usdt_5,
    grid_usdt_6,
    grid_gap_1,
    grid_gap_2,
    grid_gap_3,
    grid_gap_4,
    grid_gap_5,
  } = flat;

  const patchFlat = useCallback((patch) => {
    setFlat((prev) => ({ ...prev, ...patch }));
  }, []);

  const showAdminManualTrade = useMemo(
    () => Boolean(isAdminAccess && adminManualTradeAllowed),
    [isAdminAccess, adminManualTradeAllowed]
  );

  const fetchEscape = useCallback(
    (url, options = {}) =>
      fetch(url, {
        credentials: 'include',
        ...options,
      }),
    []
  );

  const selectableSymbols = useMemo(
    () => Array.from(new Set(symbolList.filter((sym) => sym && sym !== 'default'))),
    [symbolList]
  );

  const currentSymbolLabel = symbol && symbol !== 'default' ? symbol : 'symbol select';
  const currentBothModeLabel =
    BOTH_MODE_OPTIONS.find((o) => o.value === both_mode)?.label ?? both_mode;
  const currentVariantLabel =
    PROGRAM_VARIANTS.find((p) => p.value === program_variant)?.label ?? program_variant;
  const currentVariantTriggerLabel =
    escapeVariantShortLabel(program_variant) || currentVariantLabel;

  const applyFlatPreset = useCallback((source) => {
    if (!source || typeof source !== 'object') return;
    const base = buildEscapeDefaultFlat();
    const entryCandle = source.entry_candle || base.entry_candle;
    const closeCandle = source.close_candle || entryCandle || base.close_candle;
    const programVariant = source.program_variant || base.program_variant;
    setFlat({
      ...base,
      ...source,
      ...pickEscapeAccountFields(source),
      program_variant: programVariant,
      both_mode: source.both_mode ?? base.both_mode,
      direction: source.direction ?? base.direction,
      entry_candle: entryCandle,
      close_candle: closeCandle,
      entry_standard_2nd: 'disable',
      escape_auto_refresh_gap_pct: roundPct2(
        source.escape_auto_refresh_gap_pct ?? base.escape_auto_refresh_gap_pct
      ),
      escape_hedge_losscut_pct: roundPct2(
        source.escape_hedge_losscut_pct ?? base.escape_hedge_losscut_pct
      ),
      hedge_hold_ratio: Number(source.hedge_hold_ratio ?? base.hedge_hold_ratio) || 0,
      hedge_hold_mode: source.hedge_hold_mode ?? base.hedge_hold_mode,
      hedge_hold_roi_pct:
        source.hedge_hold_roi_pct != null && Number(source.hedge_hold_roi_pct) > 0
          ? Number(source.hedge_hold_roi_pct)
          : 0,
      hedge_hold_switch_pct:
        source.hedge_hold_switch_pct != null && Number(source.hedge_hold_switch_pct) > 0
          ? Number(source.hedge_hold_switch_pct)
          : 0,
      escape_same_bar_entry_extra: Math.max(
        0,
        Math.min(99, parseInt(source.escape_same_bar_entry_extra ?? base.escape_same_bar_entry_extra, 10) || 0)
      ),
      escape_first_entry_mode:
        source.escape_first_entry_mode === 'inflection' ? 'inflection' : 'immediate',
      plus_entry_mode:
        source.plus_entry_mode === 'confirm' ? 'confirm' : 'trigger',
      escape_entry_kp_mode: normalizeKpModeValue(
        source.escape_entry_kp_mode ?? source.plus_entry_mode
      ),
      escape_exit_kp_mode: normalizeKpModeValue(
        source.escape_exit_kp_mode ?? source.escape_entry_kp_mode ?? source.plus_entry_mode
      ),
      escape_swing_entry_kp_mode: normalizeKpModeValue(
        source.escape_swing_entry_kp_mode ?? source.escape_entry_kp_mode ?? source.plus_entry_mode
      ),
      escape_swing_exit_kp_mode: normalizeKpModeValue(
        source.escape_swing_exit_kp_mode
          ?? source.escape_swing_entry_kp_mode
          ?? source.escape_entry_kp_mode
          ?? source.plus_entry_mode
      ),
      plus_close_kp_mode: normalizeKpModeValue(
        source.plus_close_kp_mode ?? source.escape_exit_kp_mode ?? source.plus_entry_mode
      ),
    });
  }, []);

  const buildEscapePresetPayload = useCallback(() => ({ ...flat }), [flat]);

  const applyEscapePreset = useCallback(
    (payload = {}) => {
      applyFlatPreset(payload);
    },
    [applyFlatPreset]
  );

  const buildPayload = useCallback(() => {
    const body = buildEscapeSplitPayload({ ...flat, both_mode }, { both_mode, direction });
    body.symbol = symbol;
    return body;
  }, [flat, both_mode, direction, symbol]);

  const refreshLists = useCallback(() => {
    fetchEscape('/startcheck_escape/list_entry')
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setEntryItems(data || []))
      .catch(() => {});

    fetchEscape('/startcheck_escape/list_wait')
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setWaitItems(data || []))
      .catch(() => {});
  }, [fetchEscape]);

  const applyBenefitFields = useCallback((data) => {
    if (!data) return;
    setDoublePnl(Number(data.double_pnl ?? 0));
    setTotalBenefitUsdt(Number(data.total_benefit_usdt ?? 0));
    setTotalBenefitUsdtObj(Number(data.total_benefit_usdt_obj ?? 0));
    setTotalBenefitCount(Number(data.total_benefit_count ?? 0));
    setTotalCurrentCount(Number(data.total_current_count ?? 0));
    setTotalBenefitStart(Number(data.total_benefit_start ?? 0));
    setTotalContinueMode(data.total_continue_mode ?? 'disable');
    setTotalContinueDirection(data.total_continue_direction ?? 'both');
    patchFlat({
      double_benefit: Number(data.double_benefit ?? 0),
      double_continue: data.double_continue === 'enable' ? 'able' : (data.double_continue ?? 'disable'),
      single_tp_continue_mode: data.single_tp_continue_mode === 'enable'
        ? 'able'
        : (data.single_tp_continue_mode ?? 'disable'),
      total_benefit_usdt: Number(data.total_benefit_usdt ?? 0),
      total_benefit_usdt_obj: Number(data.total_benefit_usdt_obj ?? 0),
      total_benefit_count: Number(data.total_benefit_count ?? 0),
      total_current_count: Number(data.total_current_count ?? 0),
      total_benefit_start: Number(data.total_benefit_start ?? 0),
      total_continue_mode: data.total_continue_mode ?? 'disable',
      total_continue_direction: data.total_continue_direction ?? 'both',
    });
  }, [patchFlat]);

  const applyEscapeRefPx = useCallback((split, bothModeOverride) => {
    if (!split?.ok) return;
    const picked = pickEscapeRefPxFromSettings(
      split,
      bothModeOverride ?? split.both_mode ?? 'single'
    );
    setEscapeHighPx(picked.highPx);
    setEscapeLowPx(picked.lowPx);
    setEscapeHighPxSource(picked.highSource);
    setEscapeLowPxSource(picked.lowSource);
  }, []);

  const refreshEscapeRefPx = useCallback(() => {
    if (symbol === 'default') {
      alert('코인을 먼저 선택하세요!');
      return;
    }
    const urlName = symbolToUrlName(symbol);
    return fetchEscape(`/escape/settings/${urlName}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((split) => {
        if (!split?.ok) {
          alert('기준가 조회에 실패했습니다.');
          return;
        }
        applyEscapeRefPx(split);
      })
      .catch(() => alert('기준가 조회 중 오류가 발생했습니다.'));
  }, [symbol, fetchEscape, applyEscapeRefPx]);

  const loadSymbolRow = useCallback(
    (sym) => {
      if (sym === 'default') return;
      const loadSeq = ++settingsLoadSeqRef.current;
      const urlName = symbolToUrlName(sym);

      fetchEscape(`/setdata_escape/${urlName}/${SETLEVEL}`)
        .then((r) => (r.ok ? r.json() : Promise.reject()))
        .then((rows) => {
          if (loadSeq !== settingsLoadSeqRef.current) return;
          const data = rows?.[0];
          if (!data) return;
          setTradeStart(data.trade_start ?? 0);
          setBenefitStop(data.current_symbol_stop ?? 0);
          applyBenefitFields(data);
        })
        .catch(() => {});

      fetchEscape(`/escape/settings/${urlName}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((split) => {
          if (loadSeq !== settingsLoadSeqRef.current) return;
          if (!split?.ok) return;
          lastSettingsSplitRef.current = split;
          setHasAuxAccount(Boolean(split.has_aux));
          const flatFromDb = buildEscapeFlatFromSettingsSplit(split);
          if (flatFromDb) {
            applyFlatPreset(flatFromDb);
          }
          if (split.main_row) {
            applyBenefitFields(split.main_row);
          }
          applyEscapeRefPx(split);
        })
        .catch(() => {});
    },
    [fetchEscape, applyFlatPreset, applyBenefitFields, applyEscapeRefPx]
  );

  const onClickMainBalance = () => {
    if (!window.confirm('메인 잔고를 조회할까요?')) return;
    fetchEscape('/getdata_main_balance')
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          alert(data.message || '잔고 조회 오류가 발생했습니다.');
          throw new Error('balance error');
        }
        return data;
      })
      .then((result) => setMainBalance(result.main_balance))
      .catch(() => {});
  };

  const onClickSecondBalance = () => {
    if (!window.confirm('2nd 잔고를 조회할까요?')) return;
    fetchEscape('/getdata_second_balance')
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          alert(data.message || '잔고 조회 오류가 발생했습니다.');
          throw new Error('balance error');
        }
        return data;
      })
      .then((result) => setSecondBalance(result.second_balance))
      .catch(() => {});
  };

  const onClickDoublePnl = () => {
    if (symbol === 'default') {
      alert('코인을 먼저 선택하세요!');
      return;
    }
    const confirmMsg =
      both_mode === 'double'
        ? '현재 두 계정의 합산 코인 수익을 조회할까요?'
        : '현재 메인 계정 누적 수익을 조회할까요?';
    if (!window.confirm(confirmMsg)) return;
    const urlName = symbolToUrlName(symbol);
    fetchEscape(`/getdata_double_pnl_escape/${urlName}`)
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          alert(data.message || '수익 조회 오류가 발생했습니다.');
          throw new Error('pnl error');
        }
        return data;
      })
      .then((result) => setDoublePnl(result.double_pnl))
      .catch(() => {});
  };

  const onClickTotalSearch = () => {
    const benefitValue = parseFloat(totalBenefitUsdt);
    if (Number.isNaN(benefitValue) || benefitValue === 0) {
      window.alert('종합 익절 값을 넣어주세요!');
      return;
    }
    if (Number.isNaN(mainBalance) || mainBalance === 0) {
      window.alert('메인 잔고를 먼저 조회해주세요!');
      return;
    }
    if (both_mode === 'double' && (Number.isNaN(secondBalance) || secondBalance === 0)) {
      window.alert('2nd 잔고가 조회되지 않았습니다.\n더블모드 적용 시 2nd 잔고를 조회해주세요!');
      return;
    }
    const totalSum = mainBalance + secondBalance;
    setTotalBenefitUsdtObj(totalSum + (totalSum * benefitValue) / 100);
    patchFlat({ total_benefit_usdt_obj: totalSum + (totalSum * benefitValue) / 100 });
  };

  const onClickTotalStart = () => {
    const benefitValue = parseFloat(totalBenefitUsdt);
    let confirmed = false;

    if (Number.isNaN(benefitValue) || benefitValue === 0) {
      confirmed = window.confirm('종합 익절 값을 넣어주세요!');
    } else if (Number.isNaN(mainBalance) || mainBalance === 0) {
      confirmed = window.confirm('메인 잔고를 먼저 조회해주세요!');
    } else if (both_mode === 'double' && (Number.isNaN(secondBalance) || secondBalance === 0)) {
      confirmed = window.confirm(
        '2nd 잔고가 조회되지 않았습니다. 더블모드 적용시 2nd 잔고를 조회해주세요!'
      );
    } else {
      confirmed = window.confirm(
        '종합 익절을 시작할까요? 혹시라도 추가 입금시에는 종합익절을 취소한 뒤 진행하세요'
      );
    }

    if (!confirmed) return;

    const totalBalance = mainBalance + secondBalance;
    fetchEscape(
      `/setdata_total_benefit_escape/${benefitValue}/${totalBalance}/${totalBenefitCount}/${totalContinueMode}/${totalContinueDirection}`
    )
      .then((response) => {
        if (!response.ok) throw new Error('request failed');
        return response.json();
      })
      .then((result) => {
        if (result.message) alert(result.message);
        if (result?.message?.includes('시작')) {
          setTotalBenefitStart(1);
          patchFlat({ total_benefit_start: 1, total_current_count: 0 });
        }
        loadSymbolRow(symbol);
      })
      .catch(() => alert('종합 익절 시작 요청 실패'));
  };

  const onClickTotalCancel = () => {
    if (!window.confirm('종합 익절을 취소할까요?')) return;
    fetchEscape('/setdata_total_benefit_cancel_escape')
      .then((response) => {
        if (!response.ok) throw new Error('request failed');
        return response.json();
      })
      .then((result) => {
        if (result.message) alert(result.message);
        setTotalBenefitStart(0);
        patchFlat({
          total_benefit_start: 0,
          total_benefit_usdt: 0,
          total_benefit_usdt_obj: 0,
          total_benefit_count: 0,
          total_current_count: 0,
        });
        loadSymbolRow(symbol);
      })
      .catch(() => alert('종합 익절 취소 요청 실패'));
  };

  const fetchUserInfo = useCallback(() => {
    fetchEscape('/escapeversion_selected')
      .then(async (res) => {
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || '페이지 접근 권한이 없습니다.');
        }
        return data;
      })
      .then((data) => {
        const nextIsAdminAccess = Boolean(data?.is_admin_access);
        setIsAdminAccess(nextIsAdminAccess);
        setAdminManualTradeAllowed(Boolean(data?.admin_manual_trade_allowed));
        if (!nextIsAdminAccess) {
          setAdminManualTradeAllowed(false);
        }
        if (data.message !== 'ok') {
          alert(data.message);
          window.location.href = nextIsAdminAccess ? '/Adminpage' : '/SelectPage';
          return null;
        }
        return fetchEscape('/GetUserInfo');
      })
      .then(async (res) => {
        if (!res) return null;
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.message || '사용자 정보 조회에 실패했습니다.');
        }
        return data;
      })
      .then((user) => {
        if (!user) return;
        setUID(user.uid);
        setNickname(user.nickname || '');
        setMemberHasSecondBitget(
          Boolean(String(user?.uid_2 || '').trim()) && Boolean(String(user?.api_2 || '').trim())
        );
        return fetchEscape('/get_user_symbols_escape');
      })
      .then((res) => res?.json())
      .then((data) => {
        if (data?.signal) setSymbolList(data.signal);
        refreshLists();
      })
      .catch((err) => {
        alert(err?.message || '로그인 정보가 존재하지 않습니다.');
        window.location.href = isAdminAccess ? '/AdminLogin' : '/LoginAts';
      });
  }, [fetchEscape, refreshLists, isAdminAccess]);

  useEffect(() => {
    fetchUserInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!showAdminManualTrade) {
      setAdminManualModal(null);
    }
  }, [showAdminManualTrade]);

  useEffect(() => {
    loadSymbolRow(symbol);
  }, [symbol, loadSymbolRow]);

  useEffect(() => {
    if (!lastSettingsSplitRef.current) return;
    applyEscapeRefPx(lastSettingsSplitRef.current, both_mode);
  }, [both_mode, applyEscapeRefPx]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (symbolDropdownRef.current && !symbolDropdownRef.current.contains(event.target)) {
        setIsSymbolMenuOpen(false);
      }
      if (bothDropdownRef.current && !bothDropdownRef.current.contains(event.target)) {
        setIsBothMenuOpen(false);
      }
      if (variantDropdownRef.current && !variantDropdownRef.current.contains(event.target)) {
        setIsVariantMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSymbolSelect = (value) => {
    setSymbol(value);
    setIsSymbolMenuOpen(false);
  };

  const onClickCoin = (coin) => {
    handleSymbolSelect(coin);
  };

  const handleBothMenuSelect = (value) => {
    patchFlat({ both_mode: value });
    setIsBothMenuOpen(false);
  };

  const handleVariantSelect = (value) => {
    const item = PROGRAM_VARIANTS.find((p) => p.value === value);
    if (item && !item.enabled) {
      alert('다음 Phase에서 구현 예정입니다.');
      return;
    }
    const patch = { program_variant: value };
    if (value === 'dual_regression') {
      patch.direction = 'both';
      patch.escape_mode = 'off';
      patch.both_mode = 'single';
      patch.entry_standard = 'disable';
    }
    if (value === 'grid_escape') {
      patch.direction =
        direction === 'short' ? 'short' : direction === 'both' ? 'both' : 'long';
      patch.escape_mode = 'off';
      patch.both_mode = 'single';
    }
    if (value === 'plus') {
      patch.escape_mode = 'off';
      if (patch.direction === 'both') {
        patch.direction = 'long';
      }
      if (!patch.escape_auto_refresh_gap_pct || Number(patch.escape_auto_refresh_gap_pct) <= 0) {
        patch.escape_auto_refresh_gap_pct = 5;
      }
    }
    patchFlat(patch);
    setIsVariantMenuOpen(false);
  };

  const handleAddSymbol = (newSymbol) => {
    const confirmed = window.confirm(`${newSymbol}을(를) Escape 리스트에 추가할까요?`);
    if (!confirmed) return;

    fetchEscape(`/add_user_symbols_escape_front?symbol=${encodeURIComponent(newSymbol)}`)
      .then((res) => res.json())
      .then((data) => {
        if (data?.signal) setSymbolList(data.signal);
      })
      .catch(() => alert('심볼 추가 실패'));
  };

  const deleteSymbol = (targetSymbol) => {
    if (targetSymbol === 'default') {
      alert('코인을 먼저 선택하세요!');
      return;
    }
    const confirmed = window.confirm(
      `${targetSymbol} 삭제 시 봇 설정이 제거됩니다. 포지션/주문은 수동 확인하세요. 삭제할까요?`
    );
    if (!confirmed) return;

    fetchEscape(`/delete_user_symbols_escape?symbol=${encodeURIComponent(targetSymbol)}`)
      .then((res) => res.json())
      .then((data) => {
        setSymbolList(data.signal || []);
        setEntryItems((prev) => prev.filter((item) => item !== targetSymbol));
        setWaitItems((prev) => prev.filter((item) => item !== targetSymbol));
        setSymbol('default');
      })
      .catch(() => alert('심볼 삭제 실패'));
  };

  const handleOpenPopup = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const popupWidth = 300;
    const popupHeight = 320;
    const desiredTop = rect.top + scrollTop - popupHeight - 16;
    const top = Math.max(scrollTop + 20, desiredTop);
    const minLeft = scrollLeft + 16;
    const maxLeft = scrollLeft + viewportWidth - popupWidth - 16;
    const left = Math.min(Math.max(rect.left + scrollLeft, minLeft), maxLeft);
    setPopupAnchor({ top, left });
    setIsPopupOpen(true);
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    setPopupAnchor(null);
  };

  const onStart = () => {
    if (symbol === 'default') {
      alert('코인을 먼저 선택하세요!');
      return;
    }
    if (
      program_variant !== 'oneway' &&
      program_variant !== 'plus' &&
      program_variant !== 'dual_regression' &&
      program_variant !== 'grid_escape'
    ) {
      alert('지원하지 않는 프로그램 모드입니다.');
      return;
    }
    if (program_variant === 'grid_escape' && Number(entry_usdt_1) <= 0) {
      alert('그리드 1라인 USDT를 입력하세요.');
      return;
    }
    if (both_mode === 'double' && !hasAuxAccount) {
      alert('double 모드는 보조 계정(uid_2)이 연결되어 있어야 합니다.');
      return;
    }
    const isRunning = tradeStart === 1 || tradeStart === '1';
    const confirmed = window.confirm(
      isRunning
        ? `${symbol} Escape 설정을 저장하고 계속 실행할까요?`
        : `${symbol} Escape 매매를 시작할까요?`
    );
    if (!confirmed) return;
    fetchEscape('/start_escape', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(buildPayload()),
    })
      .then((r) => r.json())
      .then((data) => {
        alert(data.message);
        refreshLists();
        loadSymbolRow(symbol);
      })
      .catch(() => alert('시작 요청 실패'));
  };

  const onBenefitStop = () => {
    if (symbol === 'default') return;
    if (!window.confirm('익절 후 정리(대기) 상태로 전환할까요?')) return;
    const urlName = symbolToUrlName(symbol);
    fetchEscape(`/setdata_benefit_escape/${urlName}/${SETLEVEL}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(() => {
        setBenefitStop(1);
        refreshLists();
        loadSymbolRow(symbol);
      })
      .catch(() => alert('요청 실패'));
  };

  const onBenefitCancel = () => {
    if (symbol === 'default') return;
    if (!window.confirm('익절 후 정리를 취소할까요?')) return;
    const urlName = symbolToUrlName(symbol);
    fetchEscape(`/setdata_benefit_cancel_escape/${urlName}/${SETLEVEL}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(() => {
        setBenefitStop(0);
        refreshLists();
        loadSymbolRow(symbol);
      })
      .catch(() => alert('요청 실패'));
  };

  const onCurrentCoinClear = () => {
    if (symbol === 'default') return;
    if (!window.confirm('현재 코인 포지션을 정리할까요? (5~10초 소요될 수 있습니다)')) return;
    const urlName = symbolToUrlName(symbol);
    const mode =
      lastSettingsSplitRef.current?.both_mode
      ?? lastSettingsSplitRef.current?.main_row?.both_mode
      ?? both_mode;
    fetchEscape(`/setdata_current_coin_clear_escape/${urlName}/${mode}`)
      .then((r) => r.json())
      .then((data) => {
        alert(data.message || (data.ok ? '정리 완료' : '실패'));
        if (data.ok) setTimeout(() => window.location.reload(), 2000);
      })
      .catch(() => alert('정리 요청 실패'));
  };

  const onApplyEscapePx = async (target) => {
    if (symbol === 'default') return;
    const raw = target === 'long' ? escapeHighPx : escapeLowPx;
    const value = parseEscapeRefPxInput(raw);
    const label = target === 'long' ? '최고가(롱 기준가)' : '최저가(숏 기준가)';
    if (!Number.isFinite(value) || value < 0) {
      alert(`${label}에 0 이상의 숫자를 입력하세요.`);
      return;
    }
    const displayPx = formatEscapeRefPx(value);
    const action =
      value === 0
        ? `${label}를 0으로 초기화`
        : `${label}를 ${displayPx}(으)로 설정`;
    if (!window.confirm(`${symbol} ${action}할까요?`)) return;
    const res = await fetchEscape('/escape/escape_px_set', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ symbol, target, value }),
    });
    const data = await res.json();
    alert(data.message || (data.ok ? '적용 완료' : '실패'));
    if (data.ok) {
      if (target === 'long') {
        setEscapeHighPx(formatEscapeRefPx(value));
      } else {
        setEscapeLowPx(formatEscapeRefPx(value));
      }
      await refreshEscapeRefPx();
    }
  };

  const onClickEnd = () => {
    const uuid = cookies.uuid;
    if (!uuid) {
      alert('로그인 정보가 존재하지 않습니다.');
      return;
    }
    fetch('/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: uuid }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('로그아웃 요청 실패');
        alert('Logout is successful.');
        window.location.href = '/';
      })
      .catch((error) => console.error('로그아웃 요청 중 오류:', error));
  };

  const isRunning = tradeStart === 1 || tradeStart === '1';
  const isBenefitWaiting = benefitStop === 1 || benefitStop === '1';
  const isTotalBenefitRunning = Number(totalBenefitStart) === 1;
  const isDualRegression = program_variant === 'dual_regression';
  const isGridEscape = program_variant === 'grid_escape';
  const isPlus = program_variant === 'plus';
  const isOnewayOrGrid = program_variant === 'oneway' || isGridEscape;
  const isManualGrid =
    isGridEscape &&
    (grid_setting_mode === 'manual'
      || grid_setting_mode === 'kinpoint_manual'
      || grid_setting_mode === 'signal_manual');
  const isSignalManualGrid = isGridEscape && grid_setting_mode === 'signal_manual';
  const secondAccountBlur = both_mode !== 'double' ? 'blur(5px)' : 'none';
  const dualUnusedBlur = isDualRegression ? 'blur(5px)' : 'none';
  const plusEscapeUnusedBlur = isPlus ? 'blur(5px)' : 'none';
  const isDoubleAccountMode = both_mode === 'double';

  return (
    <main className="kp-console kp-console--escape">
      <div className="kp-console__glare" aria-hidden="true" />
      <section className="kp-console__panel">
        <header className="kp-hero">
          <p className="kp-hero__eyebrow">Bitgenstone auto trade system</p>
          <h1 className="kp-hero__title">ESCAPE SET cockpit</h1>
          <p className="kp-hero__subtitle">
            Hold와 분리된 탈출 전용 프로그램입니다. 원웨이 이스케이프(킨포인트 진입 · 탈출 스윙)를
            심볼별로 설정합니다.
          </p>
          <div className="kp-hero__meta">
            <span className="kp-hero__uid">UID {uid || '—'}</span>
            {isAdminAccess ? (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '6px 12px',
                  borderRadius: '999px',
                  background: 'rgba(255, 184, 77, 0.16)',
                  border: '1px solid rgba(255, 184, 77, 0.42)',
                  color: '#ffd18a',
                  fontSize: '12px',
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                }}
              >
                관리자 연결 모드
              </span>
            ) : null}
            <span>{nickname ? `${nickname}님 환영합니다` : '계정 정보를 불러오는 중'}</span>
            {symbol !== 'default' ? (
              <span>
                {symbol} — {isRunning ? '매매 구동중' : '정지'}
                {isBenefitWaiting ? ' · 익절후정리 대기' : ''}
              </span>
            ) : null}
          </div>
        </header>

        <div className="kp-console__layout">
          <div className="kp-console__main">
            <article className="kp-card kp-card--controls">
              <div
                className={`kp-symbol-controls kp-symbol-controls--escape${
                  isDualRegression || isGridEscape ? ' kp-symbol-controls--escape-two-picks' : ''
                }`}
              >
                <div className="kp-symbol-dropdown kp-escape-toolbar__variant" ref={variantDropdownRef}>
                  <button
                    type="button"
                    className={`kp-symbol-dropdown__trigger ${isVariantMenuOpen ? 'is-open' : ''}`}
                    onClick={() => setIsVariantMenuOpen((p) => !p)}
                    aria-haspopup="listbox"
                    aria-expanded={isVariantMenuOpen}
                  >
                    <span className="kp-symbol-dropdown__current" title={currentVariantLabel}>
                      <span className="kp-escape-toolbar__variant-label kp-escape-toolbar__variant-label--full">
                        {currentVariantLabel}
                      </span>
                      <span className="kp-escape-toolbar__variant-label kp-escape-toolbar__variant-label--short">
                        {currentVariantTriggerLabel}
                      </span>
                    </span>
                    <span className="kp-symbol-dropdown__chevron" aria-hidden="true" />
                  </button>
                  {isVariantMenuOpen && (
                    <div className="kp-symbol-dropdown__menu" role="listbox">
                      {PROGRAM_VARIANTS.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`kp-symbol-dropdown__option ${option.value === program_variant ? 'is-active' : ''}`}
                          onClick={() => handleVariantSelect(option.value)}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="kp-symbol-dropdown kp-escape-toolbar__symbol" ref={symbolDropdownRef}>
                  <button
                    type="button"
                    className={`kp-symbol-dropdown__trigger ${isSymbolMenuOpen ? 'is-open' : ''}`}
                    onClick={() => setIsSymbolMenuOpen((p) => !p)}
                    aria-haspopup="listbox"
                    aria-expanded={isSymbolMenuOpen}
                  >
                    <span className="kp-symbol-dropdown__current">{currentSymbolLabel}</span>
                    <span className="kp-symbol-dropdown__chevron" aria-hidden="true" />
                  </button>
                  {isSymbolMenuOpen && (
                    <div className="kp-symbol-dropdown__menu" role="listbox">
                      <button
                        type="button"
                        className={`kp-symbol-dropdown__option ${symbol === 'default' ? 'is-active' : ''}`}
                        onClick={() => handleSymbolSelect('default')}
                      >
                        symbol select
                      </button>
                      {selectableSymbols.length > 0 ? (
                        <>
                          <div className="kp-symbol-dropdown__divider" />
                          {selectableSymbols.map((sym) => (
                            <button
                              key={sym}
                              type="button"
                              className={`kp-symbol-dropdown__option ${sym === symbol ? 'is-active' : ''}`}
                              onClick={() => handleSymbolSelect(sym)}
                            >
                              {sym}
                            </button>
                          ))}
                        </>
                      ) : (
                        <div className="kp-symbol-dropdown__empty">등록된 심볼이 없습니다</div>
                      )}
                    </div>
                  )}
                </div>

                {!isDualRegression && !isGridEscape ? (
                  <div
                    className="kp-symbol-dropdown kp-both-dropdown kp-escape-toolbar__both"
                    ref={bothDropdownRef}
                  >
                    <button
                      type="button"
                      className={`kp-symbol-dropdown__trigger ${isBothMenuOpen ? 'is-open' : ''}`}
                      onClick={() => setIsBothMenuOpen((p) => !p)}
                      aria-haspopup="listbox"
                      aria-expanded={isBothMenuOpen}
                    >
                      <span className="kp-symbol-dropdown__current">{currentBothModeLabel}</span>
                      <span className="kp-symbol-dropdown__chevron" aria-hidden="true" />
                    </button>
                    {isBothMenuOpen && (
                      <div className="kp-symbol-dropdown__menu" role="listbox">
                        {BOTH_MODE_OPTIONS.map((option) => (
                          <button
                            key={option.value}
                            type="button"
                            className={`kp-symbol-dropdown__option ${option.value === both_mode ? 'is-active' : ''}`}
                            onClick={() => handleBothMenuSelect(option.value)}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="kp-symbol-controls__buttons">
                  <button type="button" onClick={handleOpenPopup} className="button_benefit_c_ut">
                    추 가
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteSymbol(symbol)}
                    className="button_benefit_c_ut"
                  >
                    삭 제
                  </button>
                </div>
              </div>
              <SymbolSearchPopup
                isOpen={isPopupOpen}
                onAdd={handleAddSymbol}
                onClose={handleClosePopup}
                anchorPosition={popupAnchor}
              />
            </article>

            <AdminSharedPresetPanel
              isAdminAccess={isAdminAccess}
              featureKey="escape"
              buildPresetPayload={buildEscapePresetPayload}
              applyPreset={applyEscapePreset}
              request={fetchEscape}
              reloadKey={`${uid}:${symbol}`}
              singleRow
            />

            <article className="kp-card kp-card--form">
              <div className="contentWrap_sp">
                  <div className="line" />
                  <div className="center_text_lever">Escape setting</div>

                  {isGridEscape ? (
                    <>
                      <div className="line" />
                      <div className="center_text_lever">그리드+이스케이프</div>
                      <div className="leverage_container">
                        <div className="leverage_item">direction</div>
                        <select
                          className="select_samaria"
                          value={
                            direction === 'short'
                              ? 'short'
                              : direction === 'both'
                                ? 'both'
                                : 'long'
                          }
                          onChange={(e) => patchFlat({ direction: e.target.value })}
                        >
                          <option value="both">양방향</option>
                          <option value="long">long</option>
                          <option value="short">short</option>
                        </select>
                        <div className="leverage_item">그리드 셋팅</div>
                        <select
                          className="select_samaria"
                          value={
                            GRID_SETTING_MODES.some((m) => m.value === grid_setting_mode)
                              ? grid_setting_mode
                              : 'uniform'
                          }
                          onChange={(e) => {
                            const mode = e.target.value;
                            const patch = { grid_setting_mode: mode };
                            if (mode === 'signal_manual') {
                              patch.entry_mode = 'ut';
                            }
                            patchFlat(patch);
                          }}
                        >
                          {GRID_SETTING_MODES.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                        <div className="leverage_item">탈출모드</div>
                        <select
                          className="select_samaria"
                          value={grid_escape_apply === 'skip' ? 'skip' : 'apply'}
                          onChange={(e) => patchFlat({ grid_escape_apply: e.target.value })}
                        >
                          {GRID_ESCAPE_APPLY_OPTIONS.map((m) => (
                            <option key={m.value} value={m.value}>
                              {m.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="leverage_container">
                        <div className="leverage_item">Set Leverage</div>
                        <input
                          type="number"
                          className="leverage_input_basic"
                          value={leverage_info}
                          onChange={(e) => patchFlat({ leverage_info: Number(e.target.value) })}
                        />
                        <div className="leverage_item">진입 봉</div>
                        <select
                          className="select_samaria"
                          value={entry_candle}
                          onChange={(e) => patchFlat({ entry_candle: e.target.value })}
                        >
                          {CANDLE_TF_OPTIONS.map((tf) => (
                            <option key={`grid-entry-${tf}`} value={tf}>
                              {tf}
                            </option>
                          ))}
                        </select>
                        <div className="leverage_item">탈출 봉</div>
                        <select
                          className="select_samaria"
                          value={close_candle}
                          onChange={(e) => patchFlat({ close_candle: e.target.value })}
                        >
                          {CANDLE_TF_OPTIONS.map((tf) => (
                            <option key={`grid-close-${tf}`} value={tf}>
                              {tf}
                            </option>
                          ))}
                        </select>
                      </div>
                      {isSignalManualGrid ? (
                        <GridUtSignalSettings flat={flat} patchFlat={patchFlat} />
                      ) : null}
                      <NonPlusEscapeKpSelectors flat={flat} patchFlat={patchFlat} />
                      {isManualGrid ? (
                        <div style={{ marginTop: '10px' }}>
                          <div className="center_text_lever" style={{ fontSize: '13px' }}>
                            {grid_setting_mode === 'kinpoint_manual'
                              ? '킨포인트(수동) 그리드 (USDT / 간격%)'
                              : grid_setting_mode === 'signal_manual'
                                ? '시그널(수동) 그리드 (USDT / 간격%)'
                                : '수동 그리드 (USDT / 간격%)'}
                          </div>
                          <table
                            className="admin-wz-bulk-start__entry-table"
                            style={{ width: '100%', marginTop: '8px', fontSize: '13px' }}
                          >
                            <thead>
                              <tr>
                                <th>라인</th>
                                <th>USDT</th>
                                <th>간격(%)</th>
                                <th>라인</th>
                                <th>USDT</th>
                                <th>간격(%)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {GRID_MANUAL_ROW_PAIRS.map(([left, right]) => (
                                <tr key={`${left}-${right}`}>
                                  {renderGridManualLineCells(left, flat, entry_usdt_1, patchFlat)}
                                  {renderGridManualLineCells(right, flat, entry_usdt_1, patchFlat)}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <p style={{ fontSize: '12px', opacity: 0.85, margin: '8px 0 0' }}>
                            {grid_setting_mode === 'kinpoint_manual'
                              ? '킨포인트 신호와 직전 진입가 기준 간격(%)이 충족될 때 다음 라인을 시장가로 추가합니다. USDT가 0인 라인부터 이후는 진행하지 않습니다.'
                              : grid_setting_mode === 'signal_manual'
                                ? 'UT 신호와 직전 진입가 기준 간격(%)이 충족될 때 다음 라인을 시장가로 추가합니다. USDT가 0인 라인부터 이후는 진행하지 않습니다.'
                                : 'USDT가 0인 라인부터 이후 그리드는 배치하지 않습니다.'}
                          </p>
                        </div>
                      ) : (
                        <div className="leverage_container">
                          <div className="leverage_item">라인 수</div>
                          <input
                            type="number"
                            className="leverage_input_basic"
                            value={grid_line_count}
                            onChange={(e) => patchFlat({ grid_line_count: Number(e.target.value) })}
                          />
                          <div className="leverage_item">라인당 USDT</div>
                          <input
                            type="number"
                            className="leverage_input_basic"
                            value={grid_usdt_per_line}
                            onChange={(e) =>
                              patchFlat({
                                grid_usdt_per_line: Number(e.target.value),
                                entry_usdt_1: Number(e.target.value),
                              })
                            }
                          />
                          <div className="leverage_item">간격(%)</div>
                          <input
                            type="number"
                            className="leverage_input_basic"
                            value={grid_gap_pct}
                            onChange={(e) => patchFlat({ grid_gap_pct: Number(e.target.value) })}
                          />
                        </div>
                      )}
                      <p style={{ fontSize: '12px', opacity: 0.85, margin: '8px 0 0' }}>
                        {direction === 'both'
                          ? '양방향: 무포지션에서 킨포인트 buy/sell 신호로 첫 포지션을을 정한 뒤 그리드 로직을 적용합니다. '
                          : ''}
                        첫 진입(보조+신호) 직후 지정가 그리드 배치. benefit_ratio 수익 시 즉시 익절·리셋.
                        익절 후 연속진입 적용 시 같은 진입봉에서도 HA shape·방향 일치하면 1라인 즉시 재진입.
                        {grid_escape_apply === 'skip'
                          ? ' 마지막 라인 소진 후 한 간격 더 역방향 이탈 시 손절·재진입(탈출모드 없음).'
                          : ' 마지막 라인 소진·손실 시 탈출모드 발동 후 기준가+탈출봉 shape 일치 시 스윙 진입합니다.'}
                      </p>
                    </>
                  ) : isDualRegression ? (
                    <>
                      <div className="line" />
                      <div className="center_text_lever">양방 회귀</div>
                      <div className="leverage_container">
                        <div className="leverage_item">탈출모드 발동률(%)</div>
                        <input
                          type="number"
                          className="leverage_input_basic"
                          value={dual_trigger_min_profit_pct}
                          onChange={(e) =>
                            patchFlat({ dual_trigger_min_profit_pct: Number(e.target.value) })
                          }
                        />
                        <div className="leverage_item">진입 USDT (롱·숏 각각)</div>
                        <input
                          type="number"
                          className="leverage_input_basic"
                          value={entry_usdt_1}
                          onChange={(e) => patchFlat({ entry_usdt_1: Number(e.target.value) })}
                        />
                        </div>
                      <div className="leverage_container">
                        <div className="leverage_item">Set Leverage</div>
                        <input
                          type="number"
                          className="leverage_input_basic"
                          value={leverage_info}
                          onChange={(e) => patchFlat({ leverage_info: Number(e.target.value) })}
                        />
                        <div className="leverage_item">진입 봉</div>
                        <select
                          className="select_samaria"
                          value={entry_candle}
                          onChange={(e) => patchFlat({ entry_candle: e.target.value })}
                        >
                          {CANDLE_TF_OPTIONS.map((tf) => (
                            <option key={`grid-entry-${tf}`} value={tf}>
                              {tf}
                            </option>
                          ))}
                        </select>
                        <div className="leverage_item">탈출 봉</div>
                        <select
                          className="select_samaria"
                          value={close_candle}
                          onChange={(e) => patchFlat({ close_candle: e.target.value })}
                        >
                          {CANDLE_TF_OPTIONS.map((tf) => (
                            <option key={`grid-close-${tf}`} value={tf}>
                              {tf}
                            </option>
                          ))}
                        </select>
                      </div>
                      <NonPlusEscapeKpSelectors flat={flat} patchFlat={patchFlat} />
                      <p style={{ fontSize: '12px', opacity: 0.85, margin: '8px 0 0' }}>
                        매매 시작 시 진입 USDT·레버리지로 롱·숏 수량을 각각 계산해 동시 진입합니다.
                        기준가 대비 발동률 도달 후 진입/탈출봉 킨포인트 반대신호 시 수익 다리 청산 및
                        탈출 스윙. 양쪽 정리 후 같은 자리에서 재진입합니다.
                      </p>
                    </>
                  ) : isPlus ? (
                    <>
                      <div className="leverage_container">
                        <div className="leverage_item">direction</div>
                        {both_mode === 'double' ? (
                          <input
                            className="leverage_input_basic"
                            value="메인 long · 보조 short"
                            readOnly
                          />
                        ) : (
                          <select
                            className="select_samaria"
                            value={direction === 'short' ? 'short' : 'long'}
                            disabled={both_mode === 'double'}
                            style={{ filter: both_mode === 'double' ? 'blur(5px)' : 'none' }}
                            onChange={(e) => patchFlat({ direction: e.target.value })}
                          >
                            <option value="long">long</option>
                            <option value="short">short</option>
                          </select>
                        )}
                        <div className="leverage_item">진입 USDT</div>
                        <input
                          type="number"
                          className="leverage_input_basic"
                          value={entry_usdt_1}
                          onChange={(e) => patchFlat({ entry_usdt_1: Number(e.target.value) })}
                        />
                        <div className="leverage_item">리셋 이격률(%)</div>
                        <input
                          type="number"
                          className="leverage_input_basic"
                          value={escape_auto_refresh_gap_pct}
                          onChange={(e) =>
                            patchFlat({ escape_auto_refresh_gap_pct: Number(e.target.value) })
                          }
                        />
                      </div>
                      <div className="leverage_container">
                        <div className="leverage_item">Set Leverage</div>
                        <input
                          type="number"
                          className="leverage_input_basic"
                          value={leverage_info}
                          onChange={(e) => patchFlat({ leverage_info: Number(e.target.value) })}
                        />
                        <div className="leverage_item">진입·청산 봉</div>
                        <select
                          className="select_samaria"
                          value={entry_candle}
                          onChange={(e) =>
                            patchFlat({ entry_candle: e.target.value, close_candle: e.target.value })
                          }
                        >
                          {CANDLE_TF_OPTIONS.map((tf) => (
                            <option key={`plus-entry-${tf}`} value={tf}>
                              {tf}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="leverage_container">
                        <KpModeSelect
                          label="진입 기준"
                          field="escape_entry_kp_mode"
                          value={escape_entry_kp_mode}
                          patchFlat={patchFlat}
                          syncPlusEntry
                        />
                        <KpModeSelect
                          label="정리 기준"
                          field="plus_close_kp_mode"
                          value={plus_close_kp_mode ?? escape_exit_kp_mode}
                          patchFlat={patchFlat}
                        />
                      </div>
                      <p style={{ fontSize: '12px', opacity: 0.85, margin: '4px 0 0' }}>
                        발생: 롱=숏숏롱·숏=롱롱숏 전환봉 즉시 진입. 확정: 롱=숏숏롱롱·숏=롱롱숏숏
                        (새 색 2연속) 후 진입. 정리=선택한 정리 기준 KP 반전(실제가 손익 TP/SL).
                        익절·손절 후 재진입은 기준가(TP/손절 돌파) + 진입봉 shape 일치 시 즉시 진입.
                        최초 진입만 진입 기준(확정/발생) KP 신호 사용.
                        진입봉에서는 정리(청산) 없음.
                        이격률 리셋 동일. double=메인 롱·보조 숏.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="leverage_container">
                        <div className="leverage_item">direction</div>
                        {both_mode === 'double' ? (
                          <input
                            className="leverage_input_basic"
                            value="메인 long · 보조 short"
                            readOnly
                          />
                        ) : (
                          <select
                            className="select_samaria"
                            value={direction}
                            disabled={both_mode === 'double'}
                            style={{ filter: both_mode === 'double' ? 'blur(5px)' : 'none' }}
                            onChange={(e) => patchFlat({ direction: e.target.value })}
                          >
                            <option value="both">양방향</option>
                            <option value="long">long</option>
                            <option value="short">short</option>
                          </select>
                        )}
                        <div className="leverage_item">진입 USDT</div>
                        <input
                          type="number"
                          className="leverage_input_basic"
                          value={entry_usdt_1}
                          onChange={(e) => patchFlat({ entry_usdt_1: Number(e.target.value) })}
                        />
                        <div className="leverage_item">첫진입 모드</div>
                        <select
                          className="select_samaria"
                          value={
                            escape_first_entry_mode === 'inflection' ? 'inflection' : 'immediate'
                          }
                          onChange={(e) =>
                            patchFlat({ escape_first_entry_mode: e.target.value })
                          }
                        >
                          <option value="immediate">즉시진입</option>
                          <option value="inflection">변곡진입</option>
                        </select>
                      </div>
                      <div className="leverage_container">
                        <div className="leverage_item">Set Leverage</div>
                        <input
                          type="number"
                          className="leverage_input_basic"
                          value={leverage_info}
                          onChange={(e) => patchFlat({ leverage_info: Number(e.target.value) })}
                        />
                        <div className="leverage_item">진입 봉</div>
                        <select
                          className="select_samaria"
                          value={entry_candle}
                          onChange={(e) => patchFlat({ entry_candle: e.target.value })}
                        >
                          {CANDLE_TF_OPTIONS.map((tf) => (
                            <option key={`grid-entry-${tf}`} value={tf}>
                              {tf}
                            </option>
                          ))}
                        </select>
                        <div className="leverage_item">탈출 봉</div>
                        <select
                          className="select_samaria"
                          value={close_candle}
                          onChange={(e) => patchFlat({ close_candle: e.target.value })}
                        >
                          {CANDLE_TF_OPTIONS.map((tf) => (
                            <option key={`grid-close-${tf}`} value={tf}>
                              {tf}
                            </option>
                          ))}
                        </select>
                      </div>
                      <NonPlusEscapeKpSelectors
                        flat={flat}
                        patchFlat={patchFlat}
                      />
                    </>
                  )}

                  <div className="line" />
                  <div
                    className="center_text_lever"
                    style={{ filter: isDualRegression ? dualUnusedBlur : 'none' }}
                  >
                    보조기준 · 탈출
                  </div>

                  <div
                    className="leverage_container"
                    style={{
                      filter: isDualRegression ? dualUnusedBlur : 'none',
                      pointerEvents: isDualRegression ? 'none' : 'auto',
                    }}
                  >
                    <div className="leverage_item">보조기준</div>
                    <select
                      className="select_samaria"
                      value={entry_standard}
                      onChange={(e) => patchFlat({ entry_standard: e.target.value })}
                    >
                      <option value="disable">미적용</option>
                      <option value="ema">EMA</option>
                    </select>
                    <div className="leverage_item" style={{ filter: entry_standard !== 'ema' ? 'blur(5px)' : 'none' }}>EMA 기간</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      disabled={entry_standard !== 'ema'}
                      value={ema_standard}
                      onChange={(e) => patchFlat({ ema_standard: Number(e.target.value) })}
                      style={{ filter: entry_standard !== 'ema' ? 'blur(5px)' : 'none' }}
                    />
                    <div className="leverage_item" style={{ filter: entry_standard !== 'ema' ? 'blur(5px)' : 'none' }}>보조 캔들봉</div>
                    <select
                      className="select_samaria"
                      value={candle_standard}
                      onChange={(e) => patchFlat({ candle_standard: e.target.value })}
                      style={{ filter: entry_standard !== 'ema' ? 'blur(5px)' : 'none' }}
                      disabled={entry_standard !== 'ema'}
                    >
                      <option value="1h">1h</option>
                      <option value="4h">4h</option>
                      <option value="12h">12h</option>
                      <option value="1d">1d</option>
                    </select>
                  </div>

                  <div
                    className="leverage_container"
                    style={{
                      filter: isPlus ? plusEscapeUnusedBlur : 'none',
                      pointerEvents: isPlus ? 'none' : 'auto',
                    }}
                  >
                    <div className="leverage_item">자동갱신 이격(%)</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      min="0"
                      step="0.01"
                      value={roundPct2(escape_auto_refresh_gap_pct)}
                      onChange={(e) =>
                        patchFlat({ escape_auto_refresh_gap_pct: roundPct2(e.target.value) })
                      }
                    />
                    <div className="leverage_item">단일익절(%)</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      value={benefit_ratio}
                      onChange={(e) => patchFlat({ benefit_ratio: Number(e.target.value) })}
                    />
                    {isGridEscape ? (
                      <>
                        <div className="leverage_item">익절 후 연속진입</div>
                        <select
                          className="select_samaria"
                          value={
                            single_tp_continue_mode === 'enable'
                              ? 'able'
                              : (single_tp_continue_mode ?? 'disable')
                          }
                          onChange={(e) =>
                            patchFlat({ single_tp_continue_mode: e.target.value })
                          }
                        >
                          <option value="disable">미적용</option>
                          <option value="able">적용</option>
                        </select>
                      </>
                    ) : null}
                  </div>
                  <div
                    className="leverage_container"
                    style={{
                      filter: isPlus ? plusEscapeUnusedBlur : 'none',
                      pointerEvents: isPlus ? 'none' : 'auto',
                    }}
                  >  
                    <div className="leverage_item">탈출헷지손절률(%)</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      min="0"
                      step="0.01"
                      value={roundPct2(escape_hedge_losscut_pct)}
                      onChange={(e) =>
                        patchFlat({ escape_hedge_losscut_pct: roundPct2(e.target.value) })
                      }
                    />
                    <div className="leverage_item">동일봉 추가진입(회)</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      min="0"
                      max="99"
                      step="1"
                      value={escape_same_bar_entry_extra}
                      onChange={(e) =>
                        patchFlat({
                          escape_same_bar_entry_extra: Math.max(
                            0,
                            Math.min(99, parseInt(e.target.value, 10) || 0)
                          ),
                        })
                      }
                    />
                  </div>
                  {isOnewayOrGrid ? (
                    <div
                      className="leverage_container"
                      style={{
                        filter: isPlus ? plusEscapeUnusedBlur : 'none',
                        pointerEvents: isPlus ? 'none' : 'auto',
                      }}
                    >
                      <div className="leverage_item">헷지보전 발동률(%)</div>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="leverage_input_basic"
                        value={hedge_hold_ratio}
                        onChange={(e) => {
                          const raw = String(e.target.value ?? '').trim();
                          if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                            const num = parseFloat(raw);
                            if (raw === '' || !Number.isFinite(num) || num <= 0) {
                              patchFlat({
                                hedge_hold_ratio: raw === '' ? 0 : raw,
                                hedge_hold_mode: 'disable',
                                hedge_hold_roi_pct: 0,
                                hedge_hold_switch_pct: 0,
                              });
                            } else {
                              patchFlat({ hedge_hold_ratio: raw });
                            }
                          }
                        }}
                      />
                      <div className="leverage_item">헷지보전 모드</div>
                      <select
                        className="select_samaria"
                        disabled={Number(hedge_hold_ratio) === 0}
                        value={hedge_hold_mode}
                        onChange={(e) => patchFlat({ hedge_hold_mode: e.target.value })}
                      >
                        <option value="disable">수수료보전</option>
                        <option value="able">수익보전</option>
                        <option value="hybrid_fwd">하이브리드(정)</option>
                        <option value="hybrid_rev">하이브리드(역)</option>
                      </select>
                      {isBandHybridHoldMode(hedge_hold_mode) ? (
                        <>
                          <div className="leverage_item">헷지 변환기준(%)</div>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="leverage_input_basic"
                            placeholder="예: 1"
                            title="유리한 이익이 이 %에 도달하면 수수료↔수익 보전 전환"
                            disabled={Number(hedge_hold_ratio) === 0}
                            value={formatDecimalPctInputValue(hedge_hold_switch_pct)}
                            onChange={(e) =>
                              applyDecimalPctInputChange(e.target.value, (v) =>
                                patchFlat({ hedge_hold_switch_pct: v }),
                              )
                            }
                            onBlur={() =>
                              normalizeDecimalPctOnBlur(hedge_hold_switch_pct, (v) =>
                                patchFlat({ hedge_hold_switch_pct: v }),
                              )
                            }
                          />
                        </>
                      ) : null}
                      <div className="leverage_item">수익보전 ROI(%)</div>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="leverage_input_basic"
                        placeholder="0=기본"
                        disabled={
                          Number(hedge_hold_ratio) === 0 || !bandHoldShowsProfitRoi(hedge_hold_mode)
                        }
                        value={formatDecimalPctInputValue(hedge_hold_roi_pct)}
                        onChange={(e) =>
                          applyDecimalPctInputChange(e.target.value, (v) =>
                            patchFlat({ hedge_hold_roi_pct: v }),
                          )
                        }
                        onBlur={() =>
                          normalizeDecimalPctOnBlur(hedge_hold_roi_pct, (v) =>
                            patchFlat({ hedge_hold_roi_pct: v }),
                          )
                        }
                      />
                    </div>
                  ) : null}
                  <p style={{ fontSize: '12px', opacity: 0.85, margin: '4px 0 0' }}>
                    진입 봉: 최초진입·그리드 진입·메인 익절/탈출 발동. 탈출 봉: 탈출모드 스윙
                    진입·HA 반대색 청산. 탈출헷지손절률은 탈출모드 구동 중 스윙 포지션만 손절(0=미적용).
                    헷지보전(Band 동일): 탈출 전 메인 전량 보전 · 탈출 중 스윙만 보전. 하이브리드는 변환기준(%) 도달 시 수수료↔수익 전환(전환 후 구간 되돌림 없음). 수수료보전은 진입가±0.15%.
                    헷지보전 체결 봉(entry_candle)은 메인·탈출발동 등은 대기하며, 누적 최저/최고 기준가·탈출봉 KP 등
                    조건 충족 시 그 동일봉에서 스윙(헷지) 재진입·재보전 가능(동일봉 추가진입 한도 예외).
                    동일봉 추가진입: 진입봉 기준 1회+입력값(기본 2)=총 3회까지 스윙 진입 허용(0이면 1회).
                    HA 반대색 청산 후 같은 탈출봉에서도 기준가·shape·동일봉 한도 충족 시 즉시 재진입.
                    기준가: 탈출 중 진입봉 마감마다 고/저가 누적, HA 익절 시 청산가 반영, 손절 시 누적값 유지.
                  </p>

                  <div className="leverage_container">
                    <div className="leverage_item">
                      최고가(롱 기준가){isDoubleAccountMode ? ` · ${escapeHighPxSource}` : ''}
                    </div>
                    <input
                      type="text"
                      className="leverage_input_basic"
                      value={escapeHighPx}
                      onChange={(e) => setEscapeHighPx(e.target.value)}
                      disabled={symbol === 'default'}
                    />
                    <div className="leverage_item">
                      최저가(숏 기준가){isDoubleAccountMode ? ` · ${escapeLowPxSource}` : ''}
                    </div>
                    <input
                      type="text"
                      className="leverage_input_basic"
                      value={escapeLowPx}
                      onChange={(e) => setEscapeLowPx(e.target.value)}
                      disabled={symbol === 'default'}
                    />
                  </div>
                  <p style={{ fontSize: '12px', opacity: 0.85, margin: '4px 0 0' }}>
                    최고.최저가를 직접 입력한 뒤 「적용」으로 저장합니다. 0 입력 후 적용하면 초기화됩니다.
                    「가져오기」는 저장값을 다시 불러옵니다. 스윙 진입·스윙 HA 익절 청산 시 자동 갱신됩니다.
                  </p>

                  <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                    <button
                      type="button"
                      onClick={refreshEscapeRefPx}
                      className="button_benefit_c_lever"
                      disabled={symbol === 'default'}
                    >
                      최고가/최저가 가져오기
                    </button>
                    <button
                      type="button"
                      onClick={() => onApplyEscapePx('long')}
                      className="button_benefit_c_lever"
                      disabled={symbol === 'default'}
                    >
                      최고가 적용
                    </button>
                    <button
                      type="button"
                      onClick={() => onApplyEscapePx('short')}
                      className="button_benefit_c_lever"
                      disabled={symbol === 'default'}
                    >
                      최저가 적용
                    </button>
                  </div>

                  <div className="line" />
                  <div className="center_text_lever">
                    누적익절 (더블익절){' '}
                    {isDoubleAccountMode ? '· 메인+보조 합산' : '· 메인 계정'}
                  </div>
                  <p style={{ fontSize: '12px', opacity: 0.85, margin: '4px 0 8px' }}>
                    single(롱 탈출 예): 현재 롱 미실현 + 탈출 이후 숏 청산 실현 합(스윙)이
                    목표(메인 증거금×누적익절%×레버) 이상일 때 정리. 숏 탈출이면 숏 미실현+롱 청산 실현.
                    탈출 중 단일익절(반대신호)로 메인 청산되지
                    않으며 누적익절(0%면 단일익절 %)로만 정리됩니다. double은 메인·보조 계정 각각
                    롱·숏 증거금을 합산해 서버에서 합쳐 판정합니다.
                  </p>
                  <div className="leverage_container">
                    <div className="leverage_item">누적익절설정(%)</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      value={double_benefit}
                      onChange={(e) => patchFlat({ double_benefit: Number(e.target.value) })}
                    />
                    <div className="leverage_item">현재수익(USDT)</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      value={doublePnl}
                      readOnly
                    />
                    <div className="leverage_item">연속모드</div>
                    <select
                      className="select_samaria"
                      value={double_continue === 'enable' ? 'able' : double_continue}
                      onChange={(e) => patchFlat({ double_continue: e.target.value })}
                    >
                      <option value="disable">미적용</option>
                      <option value="able">적용</option>
                    </select>
                  </div>

                  <div className="line" />
                  <div className="center_text_lever">잔고 조회 및 종합익절 셋팅</div>
                  <div className="leverage_container">
                    <div className="leverage_item">메인 잔고 조회(USDT)</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      value={mainBalance}
                      readOnly
                    />
                    <div className="leverage_item" style={{ filter: secondAccountBlur }}>
                      2nd 잔고 조회(USDT)
                    </div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      style={{ filter: secondAccountBlur }}
                      value={secondBalance}
                      readOnly
                    />
                    <div className="leverage_item">총합 잔고(USDT)</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      value={Number(mainBalance + secondBalance).toFixed(3)}
                      readOnly
                    />
                  </div>
                  <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                    <button
                      type="button"
                      onClick={onClickMainBalance}
                      className="button_benefit_c_lever"
                    >
                      메인 잔고 조회
                    </button>
                    <button
                      type="button"
                      onClick={onClickSecondBalance}
                      className="button_benefit_c_lever"
                      disabled={!isDoubleAccountMode}
                      style={{ filter: secondAccountBlur }}
                    >
                      2nd 잔고 조회
                    </button>
                    <button
                      type="button"
                      onClick={onClickDoublePnl}
                      className="button_benefit_c_lever"
                      disabled={symbol === 'default'}
                    >
                      {isDoubleAccountMode ? '더블수익 조회' : '누적수익 조회'}
                    </button>
                  </div>
                  <div className="leverage_container">
                    <div className="leverage_item">종합 익절 셋팅(%)</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      value={total_benefit_usdt}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setTotalBenefitUsdt(v);
                        patchFlat({ total_benefit_usdt: v });
                      }}
                    />
                    <div className="leverage_item">종합 익절 목표값(USDT)</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      value={Number(total_benefit_usdt_obj || totalBenefitUsdtObj).toFixed(3)}
                      readOnly
                    />
                  </div>
                  <div className="leverage_container">
                    <div className="leverage_item">종합 익절 목표 횟수</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      value={total_benefit_count}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setTotalBenefitCount(v);
                        patchFlat({ total_benefit_count: v });
                      }}
                    />
                    <div className="leverage_item">종합 익절 현재 횟수</div>
                    <input
                      type="number"
                      className="leverage_input_basic"
                      value={total_current_count}
                      readOnly
                    />
                  </div>
                  <div className="leverage_container">
                    <div className="leverage_item">종합 익절 연속 모드</div>
                    <select
                      className="select_samaria"
                      value={total_continue_mode}
                      onChange={(e) => {
                        setTotalContinueMode(e.target.value);
                        patchFlat({ total_continue_mode: e.target.value });
                      }}
                    >
                      <option value="disable">미적용</option>
                      <option value="able">적용</option>
                    </select>
                    <div className="leverage_item">연속 모드 시 진행 방향</div>
                    <select
                      className="select_samaria"
                      value={total_continue_direction}
                      onChange={(e) => {
                        setTotalContinueDirection(e.target.value);
                        patchFlat({ total_continue_direction: e.target.value });
                      }}
                    >
                      <option value="both">양방향</option>
                      <option value="long">롱</option>
                      <option value="short">숏</option>
                    </select>
                  </div>
                  <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                    <button
                      type="button"
                      onClick={onClickTotalSearch}
                      className="button_benefit_c_lever"
                    >
                      종합 익절 목표값 확인
                    </button>
                    <button
                      type="button"
                      onClick={onClickTotalStart}
                      className="button_benefit_c_lever"
                    >
                      종합 익절 시작
                    </button>
                    <button
                      type="button"
                      onClick={onClickTotalCancel}
                      className="button_benefit_c_lever"
                    >
                      종합 익절 취소
                    </button>
                  </div>
                  <div className="leverage_container">
                    <div
                      className={`leverage_item total-benefit-status ${isTotalBenefitRunning ? 'is-running' : 'is-stopped'}`}
                    >
                      {isTotalBenefitRunning ? '종합익절 구동중' : '종합익절 정지중'}
                    </div>
                  </div>

                  <div className="line" />

                  <OpenPositionsSnapshotSection
                    endpoint="/escape_open_positions_snapshot"
                    enabled={Boolean(uid)}
                    reloadKey={`${uid}-${snapshotReloadToken}`}
                    fetchImpl={fetchEscape}
                    title="메인/보조 오픈 포지션"
                    updatedAtPrefix="마지막 갱신:"
                    loadingRefreshLabel="조회 중..."
                    onSelectSymbol={onClickCoin}
                    selectedSymbol={symbol}
                    showAdminManualTrade={showAdminManualTrade}
                    onAdminNewEntry={
                      showAdminManualTrade
                        ? (e) =>
                            setAdminManualModal({
                              mode: 'new',
                              anchorRect: snapshotAnchorRect(e.currentTarget),
                            })
                        : undefined
                    }
                    onAdminAdjustLeg={
                      showAdminManualTrade
                        ? (payload) =>
                            setAdminManualModal({
                              mode: 'leg',
                              account: payload.account,
                              item: payload.item,
                              anchorRect: payload.anchorRect ?? null,
                            })
                        : undefined
                    }
                  />

                  {showAdminManualTrade && adminManualModal ? (
                    <HoldAdminManualModal
                      open
                      payload={adminManualModal}
                      onClose={() => setAdminManualModal(null)}
                      onOrderSuccess={() => {
                        setSnapshotReloadToken((t) => t + 1);
                        if (symbol && symbol !== 'default') {
                          loadSymbolRow(symbol);
                        }
                      }}
                      fetchHold={fetchEscape}
                      manualOrderEndpoint="/escape/admin_manual_order"
                      hasSecondAccount={memberHasSecondBitget}
                      pageSymbol={symbol}
                      selectableSymbols={selectableSymbols}
                      holdTargetUid={String(uid || '')}
                      isAdminAccess={isAdminAccess}
                      pageLeverage={leverage_info}
                    />
                  ) : null}

                  <div className="line" />

                  <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                    <button
                      type="button"
                      onClick={onStart}
                      className="button_benefit_c_lever"
                      disabled={symbol === 'default'}
                    >
                      {isRunning ? '설정 저장 / 재시작' : 'Escape 매매 시작'}
                    </button>
                  </div>

                  <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                    <button
                      type="button"
                      onClick={onBenefitStop}
                      disabled={!isRunning || isBenefitWaiting || symbol === 'default'}
                      className="button_benefit_c_lever"
                    >
                      익절 후 정리
                    </button>
                    <button
                      type="button"
                      onClick={onBenefitCancel}
                      disabled={!isBenefitWaiting}
                      className="button_benefit_c_lever"
                    >
                      취 소
                    </button>
                  </div>

                  <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                    <button
                      type="button"
                      onClick={onCurrentCoinClear}
                      disabled={symbol === 'default'}
                      className="bottomButton_sp_lever"
                    >
                      현재 코인 정리
                    </button>
                  </div>

                  <div className="button_benefit_leverage button_benefit_leverage--circlepro">
                    <button type="button" onClick={onClickEnd} className="bottomButton_sp_lever">
                      로그아웃
                    </button>
                  </div>
                </div>
            </article>
          </div>

          <aside className="kp-console__side">
            <article className="kp-card kp-card--list">
              <div className="list_box_title_lever">진입중인 코인 리스트</div>
              <ul className="list_box_lever">
                {entryItems.map((item, index) => (
                  <li
                    key={index}
                    className="list_item"
                    onClick={() => onClickCoin(typeof item === 'string' ? item : item.symbol)}
                  >
                    {formatEscapeListItemLabel(item)}
                  </li>
                ))}
              </ul>
            </article>

            <article className="kp-card kp-card--list">
              <div className="list_box_title_lever">익절 후 정리 리스트</div>
              <ul className="list_box_lever">
                {waitItems.map((item, index) => (
                  <li
                    key={index}
                    className="list_item"
                    onClick={() => onClickCoin(typeof item === 'string' ? item : item.symbol)}
                  >
                    {formatEscapeListItemLabel(item)}
                  </li>
                ))}
              </ul>
            </article>
          </aside>
        </div>
      </section>
    </main>
  );
}
